import type { NitroFetchOptions } from 'nitropack'
import useAccountApi from '~/services/apis/account'

export async function useAPI<T>(
	url: string,
	options: NitroFetchOptions<
		string,
		'delete' | 'get' | 'head' | 'patch' | 'post' | 'put' | 'connect' | 'options' | 'trace'
	> & { key?: string } = {}
) {
	const { cookie, storeToken } = useAuth()
	let headers: Record<string, string> = {}
	const { logout } = useAuth()
	const { GetAccountInfo } = useAccountApi()
	const { fetchPermissions } = usePermissionStore()
	const config = useRuntimeConfig()
	const errList = [
		'missing authorization',
		'unauthorized',
		'invalid token',
		'not authorized',
		'token not found',
		'user not found',
		'jwt expired'
	]

	if (cookie.value) {
		headers.Authorization = `Bearer ${cookie.value}`
		headers['Accept-Encoding'] = 'gzip, compress, br'
	}

	if (import.meta.server) {
		headers = {
			...headers,
			...useRequestHeaders(['cookie', 'referer'])
		}
	}

	const $api = $fetch.create({
		baseURL: String(config.public.APIUrlBase),
		credentials: 'same-origin',
		onRequest({ options }) {
			if (options.query) {
				// Filter out empty query params
				options.query = Object.fromEntries(
					Object.entries(options.query).filter(
						([, value]) => value !== '' && value !== null && value !== undefined
					)
				)
			}
		},
		async onResponse({ response }) {
			// This simply checks to know if the initial token was updated, update the persisted token to acomodate permissions
			const oldToken = cookie.value
			const newToken = response.headers.get('X-New-Token')
			const responseResult = response._data as IRequestResponse
			const responseData = responseResult?.data as Record<string, unknown> | undefined
			const userId = String(responseData?.id ?? '')
			if (newToken && oldToken !== newToken) {
				storeToken(newToken)
				Promise.all([GetAccountInfo(), fetchPermissions(userId)])
			}
			if (responseData?.access_token && userId) {
				const token = String(responseData.access_token)
				storeToken(token)
			}
		},
		onResponseError({ response }) {
			const { toLower } = useUtils()
			const responseResult = response._data as IRequestResponse
			const errorMessage = toLower(responseResult?.responseText ?? '')
			resolve(async () => {
				if (errList.includes(errorMessage)) {
					useNotification().alert({
						status: 401,
						message: 'Your session has expired. Please log in again.'
					})
					return logout()
				} else {
					if (Array.isArray(errorMessage)) {
						for (let index = 0; index < errorMessage.length; index++) {
							useNotification().alert({
								status: response.status === 1 ? 200 : response.status,
								message: errorMessage[index]
							})
						}
					} else {
						useNotification().alert({
							status: response.status,
							message: errorMessage
						})
					}
				}
			})
		},
		onRequestError(arg) {
			if (arg.error) {
				const errorMsg = String(arg?.error?.message).toLowerCase()
				if (errList.includes(errorMsg)) {
					useNotification().alert({
						status: 401,
						message: 'Your session has expired. Please log in again.'
					})
					logout()
					return
				}
				let displayMessage = 'Something went wrong.'
				if (!navigator.onLine) {
					displayMessage = 'You appear to be offline. Please check your connection.'
				} else if (errorMsg.includes('timeout') || errorMsg.includes('timed out')) {
					displayMessage = 'Connection timed out. Try again later.'
				} else if (errorMsg.includes('failed to fetch') || errorMsg.includes('networkerror')) {
					displayMessage = 'Network error. Service may be unavailable or connection is unstable.'
				} else {
					displayMessage = arg?.error?.message || 'Unexpected error occurred.'
				}

				useNotification().alert({
					status: 400,
					message: displayMessage
				})
			}
		}
	})
	const controller = new AbortController()
	const loading = ref(false)
	const error = shallowRef<unknown>('')
	const fn = () => {
		return $api<T>(url, {
			...options,
			headers: { ...options.headers, ...headers },
			signal: controller.signal
		})
	}
	const result = await resolve(async () => {
		return await fn()
	}, loading)

	return {
		data: toRef<T>(result as T),
		pending: loading,
		error,
		cancel: controller.abort(),
		refresh: fn
	}
}

export function onMount<T>(fn: (...arg: unknown[]) => T) {
	if (typeof fn === 'function') {
		const isInitialLoad = ref(true)

		// Handle initial load
		onMounted(async () => {
			if (isInitialLoad.value) {
				await fn()
				isInitialLoad.value = false
			}
		})

		onActivated(async () => {
			if (!isInitialLoad.value) {
				await fn()
			}
		})
	}
}
